import type { Context, MetricOptions } from "@sentry/core";
import { BaseError } from "@remi/error-core/BaseError"; // oxlint-disable-line no-restricted-imports -- existing import; the call site was never migrated
import { isSentryError } from "@remi/error-core/brand";
import { isAxiosError } from "@remi/error-core/isAxiosError";
import { ERROR_SENTRY_DATA_PROPERTY } from "@remi/error-core/types";

import { isSentryEnabled } from "../utils";
import { type CaptureExceptionFunction, getErrorReporter, type WithScopeFunction } from "./ErrorReporter";

export type SentryMetricOptions = MetricOptions;

export function reportToSentry(error: Error, extraContext?: Record<string, Context>): void {
  const reporter = getErrorReporter();

  if (!reporter) {
    return;
  }

  if (!shouldReportToSentry(error)) {
    return;
  }

  if (isAxiosError(error)) {
    reporter.withScope((scope) => {
      if (extraContext) {
        Object.entries(extraContext).forEach(([key, value]) => {
          scope.setContext(key, value);
        });
      }

      if (isAxiosError(error)) {
        let data: unknown;

        try {
          data = JSON.parse(error.config?.data);
        } catch {
          data = error.config?.data;
        }

        scope.setContext("Axios", {
          Request: {
            Data: data,
            Url: error.config?.url,
            Params: error.config?.params,
            Method: error.config?.method,
          },
          Response: {
            Data: error.response?.data,
            Text: error.response?.statusText,
            Code: error.response?.status,
          },
        });
      }

      reporter.captureException(error);
    });

    return;
  }

  if (isSentryError(error)) {
    reporter.withScope((scope) => {
      const customFingerprintPrefix = error.customFingerprintPrefix;
      const { tags, attributes, fingerprint: defaultFingerprint } = error.getSentryData();
      const fingerprint = [...(customFingerprintPrefix ? [customFingerprintPrefix] : []), ...defaultFingerprint];

      if (extraContext) {
        Object.entries(extraContext).forEach(([key, value]) => {
          scope.setContext(key, value);
        });
      }

      if (attributes) {
        scope.setExtras(attributes);
      }

      Object.entries(tags).forEach(([key, value]) => {
        scope.setTag(key, String(value));
      });
      scope.setFingerprint(fingerprint);
      reporter.captureException(error);
    });
    return;
  }

  if (error instanceof Error && ERROR_SENTRY_DATA_PROPERTY in error) {
    reporter.withScope((scope) => {
      scope.setContext("Error", {
        AdditionalData: error[ERROR_SENTRY_DATA_PROPERTY],
      });

      if (extraContext) {
        Object.entries(extraContext).forEach(([key, value]) => {
          scope.setContext(key, value);
        });
      }

      reporter.captureException(error);
    });
    return;
  }

  reporter.withScope((scope) => {
    if (extraContext) {
      Object.entries(extraContext).forEach(([key, value]) => {
        scope.setContext(key, value);
      });
    }

    reporter.captureException(error);
  });
}

export function reportToSentryMetricCounter(metric: string, counter?: number, options?: SentryMetricOptions) {
  const reporter = getErrorReporter();
  if (!reporter || !reporter.metrics) {
    return;
  }

  reporter.metrics.count(metric, counter, options);
}

export function reportToSentryMetricGauge(metric: string, value: number, options?: SentryMetricOptions) {
  const reporter = getErrorReporter();
  if (!reporter || !reporter.metrics) {
    return;
  }

  reporter.metrics.gauge(metric, value, options);
}

export function reportToSentryMetricDistribution(metric: string, value: number, options?: SentryMetricOptions) {
  const reporter = getErrorReporter();
  if (!reporter || !reporter.metrics) {
    return;
  }

  reporter.metrics.distribution(metric, value, options);
}

const shouldReportToSentry = (error: Error): boolean => {
  return !(error instanceof BaseError && error.hasBeenReportedToSentry) && isSentryEnabled();
};

export const reportCustomError = (callback: CustomErrorCallback): void => {
  const reporter = getErrorReporter();
  if (!reporter || !isSentryEnabled()) {
    return;
  }

  callback(reporter.withScope, reporter.captureException);
};

type CustomErrorCallback = (withScope: WithScopeFunction, captureException: CaptureExceptionFunction) => void;
